import * as React from "react";

import { cn } from "@/lib/cn";

const FleetMap = React.lazy(() => import("./FleetMap"));

const TAGLINE = "Monitoring for renewable energy sites";

/** Marketing-site legal pages — outside the SPA, so they open in a new tab. */
const LEGAL_LINKS = [
  { label: "Privacy policy", href: "https://renewablebot.com/privacy-policy/" },
  {
    label: "Terms & conditions",
    href: "https://renewablebot.com/terms-of-services-commercial/",
  },
] as const;

/** Shared gradient for both variants — see `--hero-*` in tokens.css. */
const HERO_GRADIENT =
  "linear-gradient(150deg, var(--hero-from) 0%, var(--hero-mid) 42%, var(--hero-to) 100%)";

export interface BrandStoryPanelProps {
  /**
   * Narrow-viewport variant: a full-bleed band above the form carrying the
   * logo and one tagline line, and nothing else. The headline, supporting
   * copy and map are not rendered at all below `lg` — the panel collapses
   * rather than disappearing, so the product is still named on a phone.
   */
  compact?: boolean;
  /** Hero headline — the board's copy per flow (MV2-907 canvas 01b). */
  headline?: string;
  /** Supporting line under the headline. Pass "" to draw none. */
  sub?: string;
  className?: string;
}

const DEFAULT_HEADLINE = "Every renewable site you look after, in one place.";
const DEFAULT_SUB =
  "Live telemetry, alarms and tickets for solar, wind, hydro and battery sites — and the controls to act on what you see.";

/**
 * BrandStoryPanel — the product-story half of the login page.
 *
 * A permanently-dark brand surface in both themes (`--hero-*` tokens), which
 * is why nothing here reads `--brand-primary`: that token flips navy → green
 * under `.dark` and would repaint the whole panel.
 *
 * The headline is a `<p>`, not a heading, on purpose. `LoginPage` already owns
 * the page's only `<h1>` ("Sign in"), and the sign-in form is the page's
 * actual purpose — promoting marketing copy above it in the heading outline
 * would put the wrong landmark first for screen-reader and keyboard users.
 */
export function BrandStoryPanel({
  compact = false,
  headline = DEFAULT_HEADLINE,
  sub = DEFAULT_SUB,
  className,
}: BrandStoryPanelProps): React.ReactElement {
  if (compact) {
    // Board `auth/LoginMobile`: 22/20/18 padding, brand row then one
    // tagline line, left-aligned. The 22px top is the board's design
    // padding, not a status-bar clearance — on a device with a notch or
    // Dynamic Island this pane sits above AppShell's safe-area handling
    // entirely (pre-auth, no shell), so without env(safe-area-inset-top)
    // added on top the logo rendered under the status bar icons.
    return (
      <div
        className={cn(
          "relative flex flex-col gap-2 px-5 pb-[18px] pt-[calc(22px+env(safe-area-inset-top))] lg:hidden",
          className,
        )}
        style={{ background: HERO_GRADIENT }}
      >
        <img
          src="/brand/SVG/Dark_Primary.svg"
          alt="re:bot"
          className="h-7 w-auto self-start"
        />
        <p className="text-[12.5px] leading-5 text-white/70">{TAGLINE}</p>
      </div>
    );
  }

  return (
    <aside
      className={cn(
        "relative hidden overflow-hidden lg:flex lg:w-1/2 lg:flex-none lg:flex-col lg:justify-between",
        "p-10 xl:px-16 xl:py-14",
        className,
      )}
      style={{ background: HERO_GRADIENT }}
    >
      {/* Decoration sits behind the copy. Suspense falls back to nothing so
          the gradient shows immediately and the map fades in when its chunk
          lands — the form never waits on it. */}
      <div aria-hidden="true" className="absolute inset-0">
        <React.Suspense fallback={null}>
          <FleetMap />
        </React.Suspense>
        <div className="fleet-map__scrim" />
      </div>

      <div className="relative z-10">
        <img
          src="/brand/SVG/Dark_Primary.svg"
          alt="re:bot"
          className="h-7 w-auto"
        />
      </div>

      {/* Board: 46 px / 600 / -0.04em headline, 17 px supporting line at
          16 px below, both capped at 560 / 520. */}
      <div className="relative z-10 max-w-[560px] py-10">
        <p
          className={cn(
            "font-semibold text-white",
            "text-[clamp(2rem,3.2vw,46px)] leading-[1.06] tracking-[-0.04em]",
          )}
        >
          {headline}
        </p>
        {sub && (
          <p className="mt-4 max-w-[520px] text-[17px] leading-[1.55] text-white/70">
            {sub}
          </p>
        )}
      </div>

      {/* `mt-auto` pins these to the foot of the panel without taking them out
          of flow, so they can never collide with the headline on a short
          viewport the way absolute positioning would. Marketing-site URLs, so
          they leave the SPA — hence the full anchors rather than <Link>. */}
      <nav
        aria-label="Legal"
        className="relative z-10 flex flex-wrap items-center gap-x-[18px] gap-y-2 text-[13px]"
      >
        {LEGAL_LINKS.map((link) => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/60 transition-colors hover:text-[color:var(--brand-secondary)] focus-visible:text-[color:var(--brand-secondary)] focus-visible:outline-none focus-visible:underline"
          >
            {link.label}
          </a>
        ))}
        <span className="flex-1" />
        <span className="text-white/60">© renewableBot 2026</span>
      </nav>
    </aside>
  );
}
